import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AbsenceFilesPlaceholderIcon,
  AppButton,
  Box,
  DownloadIcon,
  Hint,
  Inscription,
  NoSearchItemsPlaceholder,
  PaginationBar,
  PencilIcon,
  Preloader,
  SearchInput,
  SearchPlaceholderIcon,
  SelectDropdown,
  useDebounce,
  useQueryClient,
  WarningIcon,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { toNumber } from 'lodash';

import { ROUTES } from '@/common';
import { ALL_OPTION, DATE_DOTS_FORMAT, RAW_DATE_FORMAT } from '@/common/constants';
import Permissions from '@/common/permissions';
import routes from '@/common/routes';
import { DefaultSortTypesEnum,OptionKey } from '@/common/types';
import { WarnModal } from '@/components/elements';
import { SnackbarState } from '@/modules/core/core.types';
import { useBreadcrumbs, useCurrentOrganization, useHasAccess, useHistory, useSnackbar } from '@/modules/core/hooks';
import { useGetOrganizationPlansQuery } from '@/modules/employee/Employee/queries/useGetOrganizationPlans.query';
import { useListPagination } from '@/modules/employer/hooks';

import { EmployeeIdPlanIdForRequest, useGetLimitsQuery } from '../ContributionEmployeesSettings/hooks/useGetLimits.query';
import { useRecipientsStore } from '../ContributionEmployeesSettings/stores';
import { ContributionRequestStatus } from '../Contributions/contributions.types';

import { ContributionRecord } from './ContributionRecord/ContributionRecord';
import { getAmountsError, getCoveragePeriodError, getTaxYearError } from './ContributionRecord/getInputError';
import useChangeContributionStatusQuery from './queries/useChangeContributionStatus.query';
import useExportContributionActivityReportQuery from './queries/useExportContributionActivityReport.query';
import useGetContributionActivitiesQuery, { GET_CONTRIBUTION_ACTIVITIES_QUERY_KEY } from './queries/useGetContributionActivities.query';
import useGetContributionErrorsByRequestIdQuery from './queries/useGetContributionErrorsByRequestId.query';
import { GET_CONTRIBUTION_REQUEST_RECORDS_KEY, useGetContributionRequestRecords } from './queries/useGetContributionRequestRecords.query';
import useGetManualContributionCoveragePeriodQuery from './queries/useGetManualContributionCoveragePeriod.query';
import { useUpdateAllContributionRecordsSameValuesQuery } from './queries/useUpdateAllContributionRecordsSameValues.query';
import { ACTIVE_PLAN_STATUS, allValuesToggling, DEFAULT_OPTIONS, DEFAULT_PAGE, DEFAULT_PER_PAGE, isVersionControlErrorText, MAX_RECORDS_TO_UPDATE, PAGE_STEP, PER_PAGE_OPTIONS, taxYearOptions } from './common';
import { ButtonsList, HoveredIcon, RowWrapper } from './ContributionActivitiesList.styles';
import { ContributionApprovalModal } from './ContributionApprovalModal';
import { EditDataModalPopup, FormValues } from './EditDataModalPopup';
import { EditListFooter } from './EditListFooter';
import { HeaderOfList } from './HeaderOfList';
import { CONTRIBUTION_ACTIVITY_STATUS_OPTIONS,ContributionActivityStatus, CoveragePeriodRequest } from './types';
import { ChangedContributionRecord, useChangedContributionRecordsStore } from './useChangedContributionRecords.store';
import { useEditModeStore } from './useEditModeStore';
import { useEditModeWidth } from './useEditModeWidth';
import { useUpdateChangedContributionRecords } from './useUpdateChangedContributionRecords';

interface Props {
  contributionId?: string;
  submitDate?: string;
  isApprovalNeededStatus?: boolean;
}

const ContributionActivitiesList: React.FC<Props> = ({ contributionId, isApprovalNeededStatus = false, submitDate }) => {
  const isScheduledContribution = useHasAccess([{ permission: Permissions.MANAGE_SCHEDULED_CONTRIBUTIONS }]);
  const hasAccessToEditContributions = useMemo(() =>
    Boolean(isScheduledContribution && isApprovalNeededStatus),
  [isScheduledContribution, isApprovalNeededStatus]);
  useBreadcrumbs([
    {
      route: ROUTES.CONTRIBUTIONS,
      title: 'Contributions',
    },
    {
      title: submitDate || '',
    },
  ]);
  const [contributionModalAction, setContributionModalAction] = useState<boolean | undefined>(undefined);
  const [serverError, setServerError] = useState('');
  const [hasVersionControlError, setHasVersionControlError] = useState(false);
  const { contributionRequest: { requestVersion } } = useGetContributionRequestRecords(
    contributionId,
    false,
    hasAccessToEditContributions,
  );
  const [isUpdateAllRecords, setIsUpdateAllRecords] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState<OptionKey[]>(DEFAULT_OPTIONS);
  const { isEditMode, setIsEditMode } = useEditModeStore();
  const { width } = useEditModeWidth();
  const {observingOrganization: { id: organizationId, name }} = useCurrentOrganization();
  const { formattedData: plans } = useGetOrganizationPlansQuery(organizationId, [ACTIVE_PLAN_STATUS]);
  const accountsOptions = useMemo(() => {
    const options = plans?.sort((a, b) => toNumber(a.id) - toNumber(b.id)).map((item) => ({
      key: item.planCode,
      value: item.planCode,
      title: item.planCode,
    }));

    return [ALL_OPTION, ...options];
  }, [plans]);
  const accountsValues = useMemo(() => allValuesToggling(selectedAccounts, accountsOptions.length), [accountsOptions, selectedAccounts]);
  const hasAccountSelected = useMemo(
    () => accountsValues.length > 0
      && !accountsValues.some((val) => val === ALL_OPTION.value),
    [accountsValues],
  );

  const [sortBy, setSortBy] = useState<OptionKey>(DefaultSortTypesEnum.ASC);
  const {
    page,
    perPage,
    setPerPage,
    setPage,
    searchString,
    setSearchString,
  } = useListPagination(DEFAULT_PER_PAGE);
  const debouncedSearchString = useDebounce(searchString);

  const [statuses, setStatuses] = useState<OptionKey[]>(DEFAULT_OPTIONS);
  const statusValues = useMemo(() => allValuesToggling(statuses, CONTRIBUTION_ACTIVITY_STATUS_OPTIONS.length), [statuses]);
  const hasStatus = useMemo(
    () => statusValues.length > 0
      && !statusValues.some((val) => val === ALL_OPTION.value),
    [statusValues],
  );

  const [taxYears, setTaxYears] = useState<OptionKey[]>(DEFAULT_OPTIONS);
  const taxYearValues = useMemo(() => {
    if (!taxYears.length) return DEFAULT_OPTIONS;
    if (taxYears[taxYears.length - 1] === ALL_OPTION.value) return [ALL_OPTION.value as OptionKey];
    return taxYears.filter((val) => val !== ALL_OPTION.value);
  }, [taxYears]);
  const hasTaxYear = useMemo(
    () => taxYearValues.length > 0
      && !taxYearValues.some((val) => val === ALL_OPTION.value),
    [taxYearValues],
  );

  const areFiltersUsed = useMemo(() => hasStatus || hasTaxYear || hasAccountSelected || (debouncedSearchString && debouncedSearchString.length >= 3), [hasStatus, hasTaxYear, hasAccountSelected, debouncedSearchString]);

  const { isLoading, formattedData, totalElements, totalPages, numberOfElements } = useGetContributionActivitiesQuery({
    contributionId,
    page,
    size: perPage,
    ...(hasStatus ? { statuses: statusValues as ContributionActivityStatus[] } : {}),
    ...(sortBy ? {
      sortBy: sortBy as DefaultSortTypesEnum,
    } : {}),
    ...hasAccessToEditContributions ? {
      validationSortBy: DefaultSortTypesEnum.ASC,
    } : {},
    ...(hasTaxYear ? { taxYears: taxYearValues as string[] } : {}),
    ...(hasAccountSelected ? { planCodes: accountsValues as string[] } : {}),
    ...(debouncedSearchString && debouncedSearchString.length >= 3 ? { employeeName: debouncedSearchString as string } : {}),
  });
  const manualContributionCoveragePeriodParams = useMemo<CoveragePeriodRequest[]>(() => formattedData
    ?.filter((item) => item?.employeeId && item?.planCode)
    ?.map((item) => ({
      employee_id: toNumber(item.employeeId),
      plan_code: item.planCode!,
    })), [formattedData]);
  const { data: manualContributionCoveragePeriods, getCoveragePeriod } = useGetManualContributionCoveragePeriodQuery();
  useEffect(() => {
    if (submitDate && hasAccessToEditContributions && manualContributionCoveragePeriodParams?.length) {
      getCoveragePeriod({
        contributionDate: submitDate, requestData: manualContributionCoveragePeriodParams,
      });
    }
  }, [getCoveragePeriod, manualContributionCoveragePeriodParams, submitDate, hasAccessToEditContributions]);
  const { resetChangedContributionRecords, changedContributionRecords } = useChangedContributionRecordsStore();

  const handleChangeSearch = useCallback((searchValue: string) => {
    setSearchString(searchValue);
  }, [setSearchString]);

  useEffect(() => {
    setPage(1);
    resetChangedContributionRecords();
  }, [debouncedSearchString, setPage, resetChangedContributionRecords]);
  const { limits } = useRecipientsStore();
  const contributionActivities = useMemo<ChangedContributionRecord[]>(() => formattedData?.map((item) => {
    const recordWithPlanId = {
      ...item,
      ...item?.planId ? {} : {
        planId: plans?.find((plan) => plan?.planCode === item?.planCode)?.id,
      },
    };
    if (!hasAccessToEditContributions) {
      return recordWithPlanId;
    }
    const changedRecord = changedContributionRecords
      .find((item) => item.recordId === recordWithPlanId?.recordId);
    const record = changedRecord || recordWithPlanId;
    const dateError = getCoveragePeriodError(record, manualContributionCoveragePeriods);
    const amountsError = getAmountsError(record, limits);
    const taxYearError = getTaxYearError(record?.taxYear);
    return {
      ...record,
      amountsError,
      dateError,
      taxYearError,
    };
  }), [
    manualContributionCoveragePeriods,
    formattedData,
    plans,
    limits,
    changedContributionRecords,
    hasAccessToEditContributions,
  ]);

  const filteredContributionActivities = useMemo(() => {
    if (!contributionActivities) return [];
    return contributionActivities;
  }, [contributionActivities]);
  const { errorCount } = useGetContributionErrorsByRequestIdQuery(contributionId, !isEditMode && contributionModalAction);
  const hasValidationErrors = useMemo(() => hasAccessToEditContributions && contributionActivities
    ?.some((item) => item?.amountsError || item?.dateError || item?.taxYearError),
    [contributionActivities, hasAccessToEditContributions]);
  const employeeIdPlanIdForRequest = useMemo(() => contributionActivities
    ?.map((item) => ({
      employeeId: item?.employeeId || '',
      planId: item?.planId || '',
    }))?.filter((item) => item?.employeeId && item?.planId) || [] as EmployeeIdPlanIdForRequest[],
    [contributionActivities]);
  const { search } = useGetLimitsQuery();
  useEffect(() => {
    if (!hasAccessToEditContributions) return;
    search(employeeIdPlanIdForRequest);
  }, [employeeIdPlanIdForRequest, search, hasAccessToEditContributions]);
  const numberOfLoadedRecords = useMemo(() => {
    if (!numberOfElements || !totalElements || !page || !perPage || !totalPages) {
      return numberOfElements;
    }

    return (page - DEFAULT_PAGE) * perPage + numberOfElements;
  }, [numberOfElements, totalElements, page, perPage, totalPages]);
  const isAllUpdatingDisabled = useMemo(() => Boolean(totalElements && totalElements > MAX_RECORDS_TO_UPDATE),
    [totalElements]);
  const prevPage = useMemo(() => page === DEFAULT_PAGE ? page : page - PAGE_STEP, [page]);
  const onBack = useCallback(() => {
    if (prevPage < page) {
      setPage(prevPage);
    }
  }, [prevPage, page, setPage]);

  const [isExporting, setIsExporting] = useState(false);
  const exportReportCSV = useExportContributionActivityReportQuery();
  const exportReport = useCallback(async () => {
    setIsExporting(true);
    const response = await exportReportCSV({
      contributionId,
      ...(hasStatus ? { statuses: statusValues as ContributionActivityStatus[] } : {}),
      ...(hasTaxYear ? { taxYears: taxYearValues as string[] } : {}),
      ...(hasAccountSelected ? { planCodes: accountsValues as string[] } : {}),
      ...(debouncedSearchString && debouncedSearchString.length >= 3 ? { employeeName: debouncedSearchString as string } : {}),
    });
    if (response?.data) {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${name}_Contribution_Report_${dayjs(submitDate).format(DATE_DOTS_FORMAT)}.csv`);
      document.body.appendChild(link);
      link.click();
    }
    setIsExporting(false);
  }, [
    submitDate,
    name,
    accountsValues,
    exportReportCSV,
    statusValues,
    taxYearValues,
    setIsExporting,
    hasTaxYear, 
    hasStatus,
    hasAccountSelected,
    contributionId,
    debouncedSearchString,
  ]);

  const { handleAddPermanentSnackbar } = useSnackbar();
  const history = useHistory();
  const goToContributionsPage = useCallback(() => history.push(routes.CONTRIBUTIONS), [history]);
  const onSuccess = useCallback(() => {
    goToContributionsPage();
    if (contributionModalAction === true) {
      handleAddPermanentSnackbar({
        text: 'Contribution was approved',
        state: SnackbarState.positive,
        closeIcon: true,
      });
    }
    if (contributionModalAction === false) {
      handleAddPermanentSnackbar({
        text: 'Contribution was declined',
        state: SnackbarState.negative,
        closeIcon: true,
      });
    }
  }, [goToContributionsPage, contributionModalAction, handleAddPermanentSnackbar]);
  const { changeContributionStatus } = useChangeContributionStatusQuery(contributionId, onSuccess);
  const onChangeStatus = useCallback((newStatus: ContributionRequestStatus) => {
    changeContributionStatus(newStatus);
  }, [changeContributionStatus]);

  const resetFilters = useCallback(() => {
    setPage(DEFAULT_PAGE);
    setPerPage(DEFAULT_PER_PAGE);

    setSelectedAccounts(DEFAULT_OPTIONS);
    setTaxYears(DEFAULT_OPTIONS);
    setStatuses(DEFAULT_OPTIONS);
    setSortBy(DefaultSortTypesEnum.ASC);
    setSearchString('');

    resetChangedContributionRecords();
  }, [setPage, setPerPage, setSelectedAccounts, setTaxYears, setStatuses, setSortBy, resetChangedContributionRecords, setSearchString]);
  const queryClient = useQueryClient();
  const refreshPageData = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [GET_CONTRIBUTION_REQUEST_RECORDS_KEY],
    });
    queryClient.invalidateQueries({
      queryKey: [GET_CONTRIBUTION_ACTIVITIES_QUERY_KEY],
    });
  }, [queryClient]);

  const [hasSaveChangesConfirmationPopup, setHasSaveChangesConfirmationPopup] = useState(false);
  const onSuccessfullyUpdating = useCallback(() => {
      setHasSaveChangesConfirmationPopup(false);
      resetFilters();
      setIsEditMode(false);
      handleAddPermanentSnackbar({
        text: 'Changes saved',
        state: SnackbarState.positive,
        closeIcon: true,
      });
      refreshPageData();
    }, [
      setHasSaveChangesConfirmationPopup,
      resetFilters,
      setIsEditMode,
      handleAddPermanentSnackbar,
      refreshPageData,
    ]);
  const onUpdatingError = useCallback((updatingErrorText?: string) => {
      if (isVersionControlErrorText(updatingErrorText)) {
        setHasVersionControlError(true);
        return;
      }
      if (updatingErrorText) {
        setHasSaveChangesConfirmationPopup(false);
        setServerError(updatingErrorText);
        resetFilters();
        setIsEditMode(false);
        refreshPageData();
      }
    }, [
      setHasVersionControlError,
      setHasSaveChangesConfirmationPopup,
      setServerError,
      resetFilters,
      setIsEditMode,
      refreshPageData,
    ]);
  const { isLoading: isUpdating, updateChangedContributionRecords } = useUpdateChangedContributionRecords({
    contributionId,
    requestVersion,
    onSuccess: onSuccessfullyUpdating,
    onError: onUpdatingError,
  });
  const {
    updateAllContributionRecordsSameValues,
    isLoading: isAllUpdating,
  } = useUpdateAllContributionRecordsSameValuesQuery(contributionId, requestVersion, onSuccessfullyUpdating, onUpdatingError);
  const onSaveChanges = useCallback(() => {
    updateChangedContributionRecords(changedContributionRecords);
  }, [changedContributionRecords, updateChangedContributionRecords]);

  return (
    <>
      {hasSaveChangesConfirmationPopup ? (
        <WarnModal
          testId='ContributionActivitiesList_save_changes_confirmation_modal'
          visible
          header="Save Changes?"
          onSetVisible={() => setHasSaveChangesConfirmationPopup(false)}
          onCancel={() => setHasSaveChangesConfirmationPopup(false)}
          buttonText="Cancel"
          submitButtonNode={(
            <AppButton testId="ContributionActivitiesList_save_and_continue" onClick={onSaveChanges} disabled={isUpdating} width="150px">
              {isUpdating ? <Preloader size={16} thinSize={4} color="border2" /> : 'Save and Continue'}
            </AppButton>
          )}
          isPendingIcon
          {...hasValidationErrors ? {
            helptext: 'Some contribution records still contain errors.'
          } : {}}
        />
      ) : null}
      <ContributionApprovalModal
        visible={typeof contributionModalAction === 'boolean'}
        isApproval={contributionModalAction === true}
        errorCount={errorCount}
        isUpdating={isUpdating}
        onClose={() => setContributionModalAction(undefined)}
        onSubmit={onChangeStatus}
        onReviewErrors={() => setIsEditMode(true)}
      />


      {hasVersionControlError ? (
        <WarnModal
          testId='ContributionActivitiesList_version_control_warn_modal'
          visible
          header="Contribution records were updated by another user before your changes were saved"
          helptext="Please refresh the page to review the latest version."
          onSetVisible={() => setHasVersionControlError(false)}
          onCancel={() => setHasVersionControlError(false)}
          cancelButtonNode={(
            <AppButton
              testId="refresh_page"
              buttonType="secondary"
              width="150px"
              onClick={() => {
                setHasVersionControlError(false);
                resetFilters();
                refreshPageData();
                setHasSaveChangesConfirmationPopup(false);
              }}
            >
              Refresh Page
            </AppButton>
          )}
          iconNode={<WarningIcon size="48px" />}
        />
      ) : null}
      {serverError ? (
        <WarnModal
          testId='ContributionActivitiesList_error_modal'
          visible
          header="Error"
          helptext={serverError}
          onSetVisible={() => setServerError('')}
          onCancel={() => setServerError('')}
          buttonText="Close"
        />
      ) : null}

      <Box background="canvas" width="100%" data-testId="ContributionActivitiesList">
        <Box
          width={width}
          alignSelf="center"
          direction="row"
          justify="between"
          wrap
        >
          <Box direction="row" width="100%" justify="between" align="center" margin={{ vertical: 'spacing24' }}>
            <Box width="100%" direction="row" justify="between" data-testId="ContributionActivitiesList_header">
              <Inscription
                size="3xl"
                weight="bold"
                color="textBody"
                lineHeight="40px"
              >Contribution activity
              </Inscription>

              <ButtonsList direction="row" data-testId="ContributionActivitiesList_buttons">
                {isEditMode ? null : (
                  <Box onClick={exportReport} width={{ max: '110px', min: '110px' }}>
                    <AppButton
                      buttonType="secondary"
                      rightIcon={isExporting ? <Preloader size={16} thinSize={2} /> : <DownloadIcon />}
                      disabled={isExporting || !totalElements}
                      width="110px"
                    >
                      Export
                    </AppButton>
                  </Box>
                )}
                {hasAccessToEditContributions ? (
                  <>
                    {isEditMode ? (
                      <>
                        <Box width={{ max: '160px', min: '160px' }}>
                          <AppButton
                            testId="ContributionRequestRecords_cancel"
                            buttonType="secondary"
                            width="160px"
                            onClick={() => {
                              setIsEditMode(false);
                              resetFilters();
                            }}
                          >
                            Cancel
                          </AppButton>
                        </Box>
                        <Box width={{ max: '160px', min: '160px' }}>
                          <AppButton
                            testId="ContributionRequestRecords_save"
                            width="160px"
                            onClick={() => setHasSaveChangesConfirmationPopup(true)}
                            disabled={!changedContributionRecords.length}
                          >
                            Save Changes
                          </AppButton>
                        </Box>
                      </>
                    ) : (
                      <>
                        <HoveredIcon>
                          <Box
                            round="container2Round"
                            border={{ size: 'large', color: 'border' }}
                            width={{ width: '40px', min: '40px' }}
                            height={{ height: '40px', min: '40px' }}
                            justify="center"
                            align="center"
                            onClick={() => {
                              setIsEditMode(true);
                              resetFilters();
                            }}
                          >
                            <PencilIcon />
                          </Box>
                        </HoveredIcon>
                        <Box onClick={() => setContributionModalAction(false)} width={{ max: '160px', min: '160px' }}>
                          <AppButton
                            buttonType="secondary"
                            width="160px"
                            color="red"
                          >
                            Reject
                          </AppButton>
                        </Box>
                        <Box onClick={() => setContributionModalAction(true)} width={{ max: '160px', min: '160px' }}>
                          <AppButton
                            width="160px"
                          >
                            Approve
                          </AppButton>
                        </Box>
                      </>
                    )}
                  </>
                ) : null}
              </ButtonsList>
            </Box>
          </Box>

          <Box data-testId="ContributionActivitiesList_records_head" background="module" round={{ corner: 'top' }} width="100%" pad={{ horizontal: 'spacing24', bottom: 'spacing8', top: 'spacing24' }}>
            <Box height="40px" direction="row" justify='between' align="center" margin={{ bottom: 'spacing12' }}>
              <Inscription weight="bold" lineHeight="22px" color="textBody">
                Recipients: {totalElements || 0}
              </Inscription>
              <Box direction="row" data-testId="ContributionActivitiesList_filters">
                <Box margin={{ left: 'spacing12' }} width={{ min: '200px', max: '200px' }}>
                  <SearchInput
                    hasSearchingHistory
                    testId="ContributionActivitiesList"
                    disabled={!totalElements && !searchString}
                    placeholder="Search by name"
                    contentWidth={320}
                    value={searchString as string}
                    onChange={handleChangeSearch}
                  />
                </Box>
                {isEditMode ? (
                  <Box margin={{ left: 'spacing12' }} width={{ min: '126px', max: '126px' }}>
                    <SelectDropdown
                      id="perPage"
                      testId="ContributionActivities_perPage"
                      singleMode
                      ellipsisMode={false}
                      activeTitle
                      name="Show"
                      prefix="Show"
                      options={PER_PAGE_OPTIONS}
                      value={perPage}
                      onChange={(val) => {
                        setPerPage(toNumber(val));
                        setPage(1);
                        resetChangedContributionRecords();
                      }}
                    />
                  </Box>
                ) : null}

                {plans?.length ? (
                  <Box margin={{ left: 'spacing12' }} width={{ min: '160px', max: '160px' }}>
                    <SelectDropdown
                      id="accounts"
                      testId="accounts"
                      activeTitle
                      ellipsisMode
                      name="Accounts"
                      prefix="Accounts"
                      disabled={!totalElements && !hasAccountSelected}
                      options={accountsOptions}
                      values={accountsValues}
                      onChangeValues={(val: OptionKey[]) => {
                        setSelectedAccounts(val);
                        setPage(1);
                        resetChangedContributionRecords();
                      }}
                    />
                  </Box>
                ) : null}
                <Box margin={{ left: 'spacing12' }} width={{ min: '150px', max: '150px' }}>
                  <SelectDropdown
                    disabled={!totalElements && !hasTaxYear}
                    id="taxYear"
                    allMode
                    activeTitle
                    ellipsisMode
                    name="Tax year"
                    prefix="Tax year"
                    options={taxYearOptions}
                    values={taxYearValues}
                    onChangeValues={(val: OptionKey[]) => {
                      setTaxYears(val);
                      setPage(1);
                      resetChangedContributionRecords();
                    }}
                  />
                </Box>
                {isEditMode ? null : (
                  <Box margin={{ left: 'spacing12' }} width={{ min: '150px', max: '150px' }}>
                    <SelectDropdown
                      disabled={!totalElements && !hasStatus}
                      id="status"
                      allMode
                      activeTitle
                      ellipsisMode
                      name="Activity status"
                      prefix="Status"
                      options={CONTRIBUTION_ACTIVITY_STATUS_OPTIONS}
                      values={statusValues}
                      onChangeValues={(status: OptionKey[]) => {
                        setStatuses(status);
                        setPage(1);
                      }}
                    />
                  </Box>
                )}
                <Box margin={{ left: 'spacing12' }} width={{ min: '165px', max: '165px' }}>
                  <SelectDropdown
                    id="sort_by_firstName"
                    testId="ContributionActivities_sortByFilter"
                    disabled={!totalElements}
                    singleMode
                    ellipsisMode={false}
                    activeTitle
                    name="First name"
                    prefix="First name"
                    options={[
                      {
                        key: DefaultSortTypesEnum.ASC,
                        value: DefaultSortTypesEnum.ASC,
                        title: 'A-Z',
                      },
                      {
                        key: DefaultSortTypesEnum.DESC,
                        value: DefaultSortTypesEnum.DESC,
                        title: 'Z-A',
                      },
                    ]}
                    value={sortBy}
                    onChange={(val?: OptionKey) => {
                      setSortBy(val);
                      setPage(1);
                      resetChangedContributionRecords();
                    }}
                  />
                </Box>
                {isEditMode ? (
                  <Box margin={{ left: 'spacing24' }}>
                    <Hint
                      testId="update_all"
                      hideHint={!isAllUpdatingDisabled}
                      hintAlign="left"
                      hintWidth="250px"
                      marginContent="spacing8"
                      hintElement={(
                        <AppButton
                          buttonType="secondary"
                          onClick={() => setIsUpdateAllRecords(true)}
                          disabled={isAllUpdatingDisabled}
                        >
                          Update All Records
                        </AppButton>
                      )}
                    >
                      <Inscription size='small'>
                        {`This request has over ${MAX_RECORDS_TO_UPDATE} records and can’t be modified here. Please upload your changes instead.`}
                      </Inscription>
                    </Hint>
                  </Box>
                ) : null}
              </Box>
            </Box>
            <HeaderOfList isEditMode={isEditMode} />
          </Box>
        </Box>
      </Box>

      <Box
        flex
        data-testId="ContributionActivitiesList_records_data"
        direction="column"
        width={width}
        height={{ min: 'max-content' }}
        {...isEditMode ? { margin: { bottom: '40px' } } : {}}
      >
        <Box
          background={{ color: 'module' }}
          round={{ corner: 'bottom', size: 'moduleRound' }}
          pad={{ horizontal: 'spacing24', bottom: 'spacing24' }}
        >
          {filteredContributionActivities?.length || isLoading ? null : (
            <NoSearchItemsPlaceholder
              placeholderTitle={areFiltersUsed ? 'No results have been found' : 'There are no activity records yet'}
              node={areFiltersUsed ? (
                  <SearchPlaceholderIcon size="xxlarge" color="iconSecondary" />
                ) : (
                  <AbsenceFilesPlaceholderIcon size="xxlarge" color="iconSecondary" />
                )}
              description={areFiltersUsed ? 'Try to adjust your search or filter to find what you\'re looking for.' : ''}
            />
          )}

          {isLoading ? (
            <Box height={{ min: '200px' }} justify="center" align="center">
              <Preloader />
            </Box>
          ) : null}

          {!isLoading && filteredContributionActivities?.length ? (
            <Box>
              <Box>
                <RowWrapper>
                  {filteredContributionActivities.map((item) => (
                    <Box key={item.recordId}>
                      <ContributionRecord
                        contributionActivity={item}
                        isEditMode={isEditMode}
                      />
                    </Box>
                  ))}
                </RowWrapper>
              </Box>

              {totalElements && totalElements > filteredContributionActivities.length && isEditMode ? (
                <Box justify="center" align="center" margin={{ top: 'spacing16' }}>
                <Inscription color="textSecondary">
                  {numberOfLoadedRecords} records out of {totalElements}
                </Inscription>
                </Box>
              ) : null}
            </Box>
          ) : null}

          {isEditMode ? null : (
            <PaginationBar
              page={page}
              total={totalElements || 0}
              pageSize={perPage}
              onChangePage={setPage}
              onChangePageSize={setPerPage}
            />
          )}
        </Box>
      </Box>

      {isEditMode ? (
        <EditListFooter
          onCancel={() => {
            setIsEditMode(false);
            resetFilters();
          }}
          hasValidationErrors={hasValidationErrors}
          page={page}
          isLoading={isLoading}
          setPage={setPage}
          onBack={onBack}
          totalPages={totalPages}
        />
      ) : null}

      {isUpdateAllRecords || isAllUpdating ? (
        <EditDataModalPopup
          isVisible
          // hasRecordAmountsValidErrorBySum={hasAmountsValidErrorBySum}
          close={() => setIsUpdateAllRecords(false)}
          isLoading={isAllUpdating}
          onSave={(data?: FormValues) => {
            updateAllContributionRecordsSameValues({
              ...data?.employeeContributionAmount ? { ee_amount: toNumber(data.employeeContributionAmount)} : {},
              ...data?.employerContributionAmount ? { er_amount: toNumber(data.employerContributionAmount) } : {},
              ...data?.contributionDateOn ? { date: dayjs(data.contributionDateOn).format(RAW_DATE_FORMAT) } : {},
              ...data?.taxYear ? { tax_year: data.taxYear } : {},
              ...data?.taxType ? { tax_type: data.taxType } : {},
              ...data?.memo ? { memo: data.memo } : {},
            });
          }}
        />
      ) : null}
    </>
  );
};

export default ContributionActivitiesList;
